'use client';

/**
 * AIBriefingUpdateRow — single row in the "Latest updates" section of
 * {@link AIBriefingRail}. Renders a channel-tinted icon tile, a 2-line title +
 * channel label, and a relative-time stamp. The row becomes a real `<button>`
 * when `update.onClick` is provided; otherwise it's a non-interactive div.
 *
 * @see AIBriefingRail.tsx
 */

import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import MailOutlineOutlined from '@mui/icons-material/MailOutlineOutlined';
import NotificationsOutlined from '@mui/icons-material/NotificationsOutlined';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import Box from '@mui/material/Box';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React from 'react';
import { iconSizes } from '../../theme/typography';
import type { AIBriefingUpdate, AIBriefingUpdateChannel } from './AIBriefingRail';

const CHANNEL_META: Record<
  AIBriefingUpdateChannel,
  { Icon: typeof MailOutlineOutlined; paletteKey: 'warning' | 'info' | 'secondary' | 'text' }
> = {
  EMAIL: { Icon: MailOutlineOutlined, paletteKey: 'warning' },
  IN_APP: { Icon: NotificationsOutlined, paletteKey: 'info' },
  SECURITY: { Icon: ShieldOutlined, paletteKey: 'secondary' },
  AUDIT: { Icon: HistoryOutlined, paletteKey: 'text' },
};

function ChannelIconTile({ channel }: { channel: AIBriefingUpdateChannel }) {
  const theme = useTheme();
  const { Icon, paletteKey } = CHANNEL_META[channel];
  const isDark = theme.palette.mode === 'dark';
  const tone =
    paletteKey === 'text'
      ? theme.palette.text.secondary
      : (theme.palette[paletteKey]?.main ?? theme.palette.text.secondary);
  return (
    <Box
      aria-hidden
      sx={{
        width: 22,
        height: 22,
        flexShrink: 0,
        borderRadius: 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: alpha(tone, isDark ? 0.18 : 0.12),
        color: tone,
      }}
    >
      <Icon sx={{ fontSize: iconSizes.xs }} />
    </Box>
  );
}

export interface AIBriefingUpdateRowProps {
  update: AIBriefingUpdate;
  showDivider: boolean;
  /** Pre-localized "Channel: EMAIL" / "Channel: IN_APP" label. */
  channelLabel: string;
}

export function AIBriefingUpdateRow({
  update,
  showDivider,
  channelLabel,
}: AIBriefingUpdateRowProps): React.JSX.Element {
  const isInteractive = typeof update.onClick === 'function';
  const rowSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    width: '100%',
    py: 1,
    px: 0,
    border: 'none',
    background: 'transparent',
    textAlign: 'left',
    color: 'text.primary',
    cursor: isInteractive ? 'pointer' : 'default',
    minHeight: 44,
    borderBottom: showDivider ? '1px solid' : 'none',
    borderColor: showDivider ? 'divider' : 'transparent',
    transition: 'background-color 0.15s ease',
    '&:hover': isInteractive ? { bgcolor: 'action.hover' } : undefined,
    '&:focus-visible': isInteractive
      ? { outline: (theme: Theme) => `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 }
      : undefined,
  };
  const inner = (
    <>
      <ChannelIconTile channel={update.channel} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {update.title}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {channelLabel}
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
        {update.relativeTime}
      </Typography>
    </>
  );
  return isInteractive ? (
    <Box component="button" type="button" onClick={update.onClick} sx={rowSx}>
      {inner}
    </Box>
  ) : (
    <Box sx={rowSx}>{inner}</Box>
  );
}

AIBriefingUpdateRow.displayName = 'AIBriefingUpdateRow';

export default AIBriefingUpdateRow;
